import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
} from '@mui/material';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import FullscreenExitIcon from '@mui/icons-material/FullscreenExit';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';

import API_BASE_URL from '../../../../Constants';

import { RenderFields } from '../../../setup/components/Customer/RenderFields';
import CustomerAccordion from '../../../setup/components/Customer/accordion/CustomerAccordion';

import {
  agencyGeneralFields,
  agencyAddressFields,
  agencyClasses,
  agencyContactFields,
  agencyViolationsFields,
  agencyDispatchSubClass,
  agencyDefaultFormData,
} from './config';

const AddAgencyModal = ({ open, handleClose, allAgencies, updateAll }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [agency, setAgency] = useState(agencyDefaultFormData);

  // We'll store the numeric key for dispatchClass
  const [subClassOptionsKey, setSubClassOptionsKey] = useState(null);

  // Whenever agency.dispatchClass changes => reset dispatchSubClass
  useEffect(() => {
    if (agency && Number(agency.dispatchClass) !== subClassOptionsKey) {
      setSubClassOptionsKey(Number(agency.dispatchClass));
      // Reset to null so user must pick a new sub-class
      setAgency((prev) => ({ ...prev, dispatchSubClass: null }));
    }
  }, [agency, subClassOptionsKey]);

  // Helper to push the newly created agency into `allAgencies`
  const updateAgency = (newAgency) => {
    const all = [...allAgencies];
    all.push(newAgency);
    updateAll(all);
  };

  // Save => POST to the server
  const handleSaveChanges = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/inspections/agency/createAgency`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(agency),
      });
      if (!response.ok) {
        throw new Error('Failed to create agency');
      }
      const data = await response.json();
      updateAgency(data);
      handleClose();
    } catch (error) {
      console.error('Error creating agency:', error);
    }
  };

  // Basic form field update
  const handleFieldChange = (e) => {
    const { name, value } = e.target;
    setAgency((prev) => ({ ...prev, [name]: value }));
  };

  // Split any array of fields into two halves (for layout)
  const splitFields = (fieldsArray) => {
    const midIndex = Math.ceil(fieldsArray.length / 2);
    return [fieldsArray.slice(0, midIndex), fieldsArray.slice(midIndex)];
  };

  // If modal is not open => return nothing
  if (!open) return null;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth={false}
      PaperProps={{
        style: {
          width: isExpanded ? '90%' : '1000px',
          maxHeight: '90vh',
          minHeight: '90vh',
        },
      }}
      fullWidth
      style={{ zIndex: 1400 }}
    >
      {/* Modal Header */}
      <div className="sticky top-0 bg-white z-10">
        <div className="flex justify-between items-center p-4">
          {/* Left side: Back button + Title */}
          <div className="flex items-center space-x-4 flex-grow">
            <button
              onClick={handleClose}
              className="p-2 rounded-full hover:bg-gray-200 transition"
              aria-label="Back"
            >
              <ArrowBackIcon />
            </button>
            <h2 className="text-2xl font-bold text-left">Add Agency</h2>
          </div>
          {/* Right side: Expand / Shrink */}
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className="p-2 rounded-full hover:bg-gray-200 transition"
            aria-label={isExpanded ? 'Shrink Modal' : 'Expand Modal'}
          >
            {isExpanded ? <FullscreenExitIcon /> : <FullscreenIcon />}
          </button>
        </div>

        {/* Save button */}
        <div className="flex space-x-4 p-4">
          <button
            onClick={handleSaveChanges}
            className="px-6 py-2 bg-sss-accent-yel text-sss-blue-sel font-bold rounded-md hover:bg-yellow-600 transition"
          >
            Save
          </button>
        </div>
      </div>

      {/* Modal Content */}
      <DialogContent
        dividers
        sx={{
          padding: 0,
          maxHeight: '90vh',
          overflowY: 'auto',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <div className="p-4 space-y-5 rounded-2xl">
          {/* 1) General Info */}
          {(() => {
            const [genLeft, genRight] = splitFields(agencyGeneralFields);
            return (
              <CustomerAccordion label="General Info">
                <div className={`flex ${isExpanded ? 'flex-row' : 'flex-col md:flex-row'} gap-4`}>
                  <div className="w-full">
                    <RenderFields
                      fields={genLeft}
                      formData={agency}
                      handleChange={handleFieldChange}
                      readOnly={false}
                    />
                  </div>
                  <div className="w-full">
                    <RenderFields
                      fields={genRight}
                      formData={agency}
                      handleChange={handleFieldChange}
                      readOnly={false}
                    />
                  </div>
                </div>
              </CustomerAccordion>
            );
          })()}

          {/* 2) Address Info */}
          {(() => {
            const [addrLeft, addrRight] = splitFields(agencyAddressFields);
            return (
              <CustomerAccordion label="Address Info">
                <div className={`flex ${isExpanded ? 'flex-row' : 'flex-col md:flex-row'} gap-4`}>
                  <div className="w-full">
                    <RenderFields
                      fields={addrLeft}
                      formData={agency}
                      handleChange={handleFieldChange}
                      readOnly={false}
                    />
                  </div>
                  <div className="w-full">
                    <RenderFields
                      fields={addrRight}
                      formData={agency}
                      handleChange={handleFieldChange}
                      readOnly={false}
                    />
                  </div>
                </div>
              </CustomerAccordion>
            );
          })()}

          {/* 3) Contact Info */}
          {(() => {
            const [contactLeft, contactRight] = splitFields(agencyContactFields);
            return (
              <CustomerAccordion label="Contact Info">
                <div className={`flex ${isExpanded ? 'flex-row' : 'flex-col md:flex-row'} gap-4`}>
                  <div className="w-full">
                    <RenderFields
                      fields={contactLeft}
                      formData={agency}
                      handleChange={handleFieldChange}
                      readOnly={false}
                    />
                  </div>
                  <div className="w-full">
                    <RenderFields
                      fields={contactRight}
                      formData={agency}
                      handleChange={handleFieldChange}
                      readOnly={false}
                    />
                  </div>
                </div>
              </CustomerAccordion>
            );
          })()}

          {/* 4) Violations Info */}
          {(() => {
            const [violLeft, violRight] = splitFields(agencyViolationsFields);
            const [proposalFields, dispatchFields] = splitFields(agencyClasses);

            return (
              <CustomerAccordion label="Violations Info">
                <div className={`flex ${isExpanded ? 'flex-row' : 'flex-col md:flex-row'} gap-4`}>
                  <div className="w-full">
                    <RenderFields
                      fields={violLeft}
                      formData={agency}
                      handleChange={handleFieldChange}
                      readOnly={false}
                    />
                  </div>
                  <div className="w-full">
                    <RenderFields
                      fields={violRight}
                      formData={agency}
                      handleChange={handleFieldChange}
                      readOnly={false}
                    />
                  </div>
                </div>

                <div className={`flex ${isExpanded ? 'flex-row' : 'flex-col md:flex-row'} gap-4`}>
                  <div className="w-full">
                    <RenderFields
                      fields={proposalFields}
                      formData={agency}
                      handleChange={handleFieldChange}
                      readOnly={false}
                    />
                  </div>
                  <div className="w-full">
                    <RenderFields
                      fields={dispatchFields}
                      formData={agency}
                      handleChange={handleFieldChange}
                      readOnly={false}
                    />

                    {/* Sub-Class Dropdown */}
                    <div key="dispatchSubClass" className="flex flex-col w-full">
                      <div className="flex justify-between items-center w-full pb-2">
                        <label
                          className="font-semibold p-2 w-1/3 h-16 flex items-center"
                          style={{ lineHeight: '1.2', wordBreak: 'break-word' }}
                        >
                          Dispatch SubClass
                          <span className="flex-grow border-dotted border-b-4 border-gray-200 m-2"></span>
                        </label>
                        <select
                          name="dispatchSubClass"
                          className="border-gray-300 flex-grow p-2 rounded-md my-auto bg-gray-100"
                          value={agency.dispatchSubClass || ''}
                          onChange={handleFieldChange}
                          disabled={false}
                        >
                          <option value="">Select an option</option>
                          {(
                            (subClassOptionsKey &&
                              agencyDispatchSubClass.options[subClassOptionsKey]) ||
                            []
                          ).map((option) => (
                            <option key={option.value} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                  </div>
                </div>
              </CustomerAccordion>
            );
          })()}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default AddAgencyModal;
